import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { navigate } from 'gatsby';
import MediaQuery from 'react-responsive';
import queryString from 'query-string';
import { captureMessage, setContext } from '@sentry/gatsby';
import { useBranchContext } from '../../../../../context/BranchContext';

// Components
import { Container, Button, Row, Column, Modal, Select, TextInput, TextArea, Checkbox, Alert, List, Icon } from '@pella/sash-framework';
import { Breadcrumbs, LoadingOverlay } from '../../../../';
import { removePhoneNumberFormat, getSessionStorage, getCountryFromPostal, initDataLayer, setCookie, getCookie, monetateDataLayer, getLocalStorage } from '../../../../utilities/functions';
import { isValidPostal } from '../../../../utilities/validations';
import AddressValidation from '../../../../common/address-validation/v3';
import RTACalendar from '../calendar';
import RTACalendarTimeWrapper from '../calendar/calendar-time-wrapper';
import RTATimeslotTable from '../timeslot/timeslot-table';
import BranchOffer from '../../../../common/branch-offer';
import smoothScroll from '../../../../common/smooth-scroll';
import LocalNavTop from '../../../../common/local-nav-top';

// Styles
import styles from './index.module.scss';

// Initial Calendar
import { initialCalendar } from '../calendar/initial-calendar';

// API Calls
import { resetRTACalls, getCalendar, appointment, timeblock, nextId, storeAppointment, randomId } from '../../../../../actions/rta-actions';

// Constants
import { emailRegex, phoneReplace, RTA_ERRORS, RTA_COMMON } from '../../../../utilities/constants';
import { getBranchID } from '../../../../utilities/branches';
import { getShowroomsByIP } from '../../../../../actions/location-actions';

const formBaseState = {
	postal: '',
	firstname: '',
	lastname: '',
	email: '',
	phone: '',
	phonetype: '',
	windows: '',
	doors: '',
	// Hidden field to help scan for bots
	householdPets: '',
	previousAddress: {
		line_1: '',
		line_2: '',
		city: '',
		state: '',
		postcode: '',
	},
};

const ScheduleConsultation = ({ data, location, layout = 'default', ctaText = 'See Available Appointments', referrer }) => {
	const rtaData = useSelector((state) => state.rtaData);
	const signInState = useSelector((state) => state.signInState);
	const locations = useSelector((state) => state.locations);
	const dispatch = useDispatch();
	// Component mounted state
	const [mounted, setMounted] = useState(false);
	// Clock state
	const timeoutWarning = useRef(null);
	const [timeLeft, setTimeLeft] = useState(0);
	const clockRef = useRef(timeLeft);
	clockRef.current = timeLeft;
	const [clock, setClock] = useState(false);
	// Form complete state
	const [complete, setComplete] = useState(false);
	// Manual entered form state
	const [formData, setFormData] = useState({
		...formBaseState,
		previousZip: '',
		comments: '',
		privacyCheck: false,
		contactCheck: true,
	});
	// Address state
	const [address, setAddress] = useState({
		addressValidated: false,
		userValidated: false,
		userEntered: false,
	});
	// API data state
	const [apiData, setApiData] = useState({
		initialCalendar: initialCalendar(),
		timeslots: [],
		isInitial: true,
		territoryGroup: '',
		timezoneCode: '',
		hasShowroom: false,
		waitingOnShowrooms: true,
		branchID: '',
		recID: '',
		// branchContactNumber: '',
		appointmentStatus: '',
	});
	// Selected appointment state
	const [apptInfo, setApptInfo] = useState({
		appointmentType: '', // ['home', 'showroom', 'preference', 'nrt', 'virtual']
		startDate: '',
		endDate: '',
		preferenceDay: '',
		preferenceTime: '',
		arrayStartPosition: null,
		timeslotLength: null,
		selectedDay: 0,
		selectedTimeslot: -1,
		firstCalendarLoad: true,
	});
	// Additional showroom appointment state
	const [showroomInfo, setShowroomInfo] = useState({
		showroomName: '',
		showroomAddress: '',
		showroomCity: '',
		showroomState: '',
		showroomCountry: '',
		showroomZip: '',
		showroomEmail: '',
		showroomPhone: '',
		showroomWeekHours: '',
		showroomSatHours: '',
		showroomSunHours: '',
	});
	// Error state
	const [errors, setErrors] = useState({});
	// Form status state
	const [formStatus, setFormStatus] = useState({
		loading: false,
		submitDisabled: true,
		showroomAlert: false,
		hideShowrooms: true,
		loadDisabled: true,
		tempStore: false,
		tempObject: {},
	});
	// Modals
	const [modalOpen, setModalOpen] = useState(false);
	const [sessionTimerModal, setSessionTimerModal] = useState(false);
	const [isDesignworksLead, setIsDesignworksLead] = useState(false);
	const [loading, setLoading] = useState({ active: false, msg: null });
	// Updates for Address Validation v3
	const [addressErrors, setAddressErrors] = useState({});
	const [addressComplete, setAddressComplete] = useState(false);
	const [addressModified, setAddressModified] = useState(false);
	const [forceAddressValidation, setForceAddressValidation] = useState(false);
	const [usePreviousAddress, setUsePreviousAddress] = useState(false);
	// For showing calendar on click
	const [showCalendar, setShowCalendar] = useState(false);

	// Local nav
	const [branchData, setBranchData] = useState({});
	const { currentBranch } = useBranchContext();

	useEffect(() => {
		dispatch(resetRTACalls());
		let postalParam = '';
		// PW-5453 - Added virtual
		let appointmentType = RTA_COMMON.TYPE.HOME;
		if (window !== undefined) {
			if (!apiData.recID) {
				dispatch(nextId());
			}
			const queryParams = queryString.parse(window.location.search);
			if (queryParams.postalCode) {
				postalParam = queryParams.postalCode;
			}
			// PW-5453 - Added virtual
			if (queryParams.appointmentType && queryParams.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
				appointmentType = RTA_COMMON.TYPE.VIRTUAL;
			}
		}
		if (postalParam) {
			if (isValidPostal(postalParam)) {
				setMounted(true);
				setFormStatus({
					...formStatus,
					loading: true,
					loadDisabled: true,
				});
				setApiData({ ...apiData, isInitial: true });
				// appointmentType was added here for PW-5453 - Added virtual
				setApptInfo({
					...apptInfo,
					appointmentType: appointmentType,
					waitingOnShowrooms: true,
					aptSelected: appointmentType,
				});
			} else {
				setMounted(true);
				setFormStatus({ ...formStatus, loadDisabled: false });
				setApptInfo({
					...apptInfo,
					appointmentType: appointmentType,
					aptSelected: appointmentType,
				});
			}
		} else {
			setMounted(true);
			setApptInfo({
				...apptInfo,
				appointmentType: appointmentType,
				aptSelected: appointmentType,
			}); // PW-5453 - Added virtual
		}
		if (layout === 'default') {
			monetateDataLayer('setPageType', 'RTA');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		if (location.state?.designworks_lead) {
			setIsDesignworksLead(true);
		}
		if (location.state?.userAddress) {
			setAddress(location.state.userAddress);
			setFormData((curr) => ({
				...curr,
				referrer: location.state.referrer,
				previousAddress: {
					line_1: location.state.userAddress.address1,
					line_2: '',
					city: location.state.userAddress.city,
					state: location.state.userAddress.state,
					postcode: location.state.userAddress.postalCode,
				},
			}));

			setLoading((curr) => ({ ...curr, active: true }));
			setTimeout(() => {
				// give time for the address validation component to catch up
				document.getElementById('js-addressSubmit')?.click();
				setLoading((curr) => ({ ...curr, active: false }));
			}, 1000);
		}
	}, [location.state]);

	useEffect(() => {
		if (layout === 'modal') return;

		const branchID = getBranchID(currentBranch, locations?.showrooms);

		if (!branchID) {
			dispatch(getShowroomsByIP());
		} else {
			const branch = data?.allContentstackBranchData?.nodes?.find((branch) => {
				if (typeof branchID === 'object') {
					return branchID.some((id) => branch.branch_retail?.includes(id));
				} else {
					return branch.branch_retail?.includes(branchID);
				}
			});

			if (branch) {
				setBranchData(branch);
			}
		}
	}, [currentBranch, data?.allContentstackBranchData?.nodes, dispatch, layout, locations?.showrooms]);

	// START: Might need to add checks inside a "mounted" if check
	useEffect(() => {
		// Checking for opp number API response
		if (!apiData.recID && rtaData.OppNo && apiData.recID !== rtaData.OppNo) {
			setApiData({ ...apiData, recID: rtaData.OppNo });
			dispatch(resetRTACalls()); // Need to remove this oppNumber from redux state, or sometimes oppNumber doesn't always change on refresh
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.OppNo]);

	useEffect(() => {
		// Checking for resource not available error message
		if (rtaData.errorMessage === RTA_ERRORS.RESOURCE_UNAVAILABLE) {
			refreshCalendar();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.errorMessage]);

	// Calendar Timings & Errors
	const [speedcurveCalendarTimingStart, setSpeedcurveCalendarTimingStart] = useState(null);
	useEffect(() => {
		if (speedcurveCalendarTimingStart !== null && (rtaData.status === 'success' || rtaData.status === 'failed')) {
			let LUX = window.LUX || {};
			let timing = (performance.now() - speedcurveCalendarTimingStart) / 1000;

			if (rtaData?.status === 'success' && typeof LUX.addData === 'function') {
				LUX.addData('rtaCalenderLoadTime', timing);
				LUX.addData('rtaCalendarLoadFailed', false);
			} else if (rtaData?.status === 'failed' && typeof LUX.addData === 'function') {
				LUX.addData('rtaCalenderLoadTime', timing);
				LUX.addData('rtaCalendarLoadFailed', true);
				setContext('Error Data', {
					httpStatusCode: rtaData.httpStatusCode,
					appointmentType: rtaData.AppointmentType,
					branchID: rtaData.BranchID,
					error: rtaData.errorMessage,
				}),
					captureMessage('RTA Alerts - Get Calendar Error FE');
			}
			setSpeedcurveCalendarTimingStart(null);
		}
	}, [rtaData, speedcurveCalendarTimingStart]);

	useEffect(() => {
		// Checking for calendar response
		let newApiData = {};
		let newApptInfo = {};
		if (apiData.recID) {
			if (rtaData.TerritoryZone && rtaData.TimezoneInfo && rtaData.BranchID) {
				if (rtaData.TerritoryZone === RTA_COMMON.ZONE.RT_ZONE) {
					if (rtaData.Timeslots && rtaData.Timeslots.length !== 0) {
						let day = parseInt(rtaData?.Timeslots[0]?.START_DATE?.split('T')[0]?.split('-')[2]);
						let length = 0;
						for (let i = 0; i < rtaData.Timeslots.length; i++) {
							if (parseInt(rtaData?.Timeslots[i]?.START_DATE?.split('T')[0]?.split('-')[2]) !== day) {
								break;
							}
							length++;
						}
						newApiData = {
							timeslots: rtaData.Timeslots,
							territoryGroup: rtaData.TerritoryZone,
							timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
							branchID: rtaData.BranchID,
							isInitial: false,
						};
						newApptInfo = {
							selectedDay: parseInt(day),
							arrayStartPosition: 0,
							timeslotLength: length,
							selectedTimeslot: 0,
							appointmentType: [RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType) ? apptInfo.appointmentType : RTA_COMMON.TYPE.HOME,
							startDate: rtaData.Timeslots[0].START_DATE,
							endDate: rtaData.Timeslots[0].END_DATE,
						};
						setTimer(); // Set/Reset timer
						setApiData({
							...apiData,
							...newApiData,
						});
						setApptInfo({
							...apptInfo,
							...newApptInfo,
						});
					} else if (rtaData.Timeslots && rtaData.Timeslots.length === 0) {
						newApiData = {
							timeslots: rtaData.Timeslots,
							territoryGroup: rtaData.TerritoryZone,
							timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
							branchID: rtaData.BranchID,
							isInitial: false,
						};
						newApptInfo = {
							selectedDay: 0,
							arrayStartPosition: null,
							timeslotLength: null,
							selectedTimeslot: -1,
							appointmentType: RTA_COMMON.TYPE.PREFERENCE,
							startDate: '',
							endDate: '',
						};
						clearTimer();
						setApiData({
							...apiData,
							...newApiData,
						});
						setApptInfo({
							...apptInfo,
							...newApptInfo,
						});
					}
				} else {
					newApiData = {
						timeslots: rtaData.Timeslots,
						territoryGroup: rtaData.TerritoryZone,
						timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
						branchID: rtaData.BranchID,
						isInitial: false,
					};
					newApptInfo = {
						selectedDay: 0,
						arrayStartPosition: null,
						timeslotLength: null,
						selectedTimeslot: -1,
						appointmentType: RTA_COMMON.TYPE.NRT,
						startDate: '',
						endDate: '',
					};
					clearTimer();
					setApiData({
						...apiData,
						...newApiData,
					});
					setApptInfo({
						...apptInfo,
						...newApptInfo,
					});
				}
			} else if (!rtaData.TerritoryZone && apiData.territoryGroup !== rtaData.TerritoryZone && rtaData.status === 'failed') {
				newApiData = {
					timeslots: rtaData.Timeslots || [],
					territoryGroup: null,
					timezoneCode: rtaData?.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
					branchID: rtaData.BranchID,
					isInitial: false,
				};
				newApptInfo = {
					selectedDay: 0,
					arrayStartPosition: null,
					timeslotLength: null,
					selectedTimeslot: -1,
					appointmentType: RTA_COMMON.TYPE.NRT,
					startDate: '',
					endDate: '',
				};
				clearTimer();
				setApiData({
					...apiData,
					...newApiData,
				});
				setApptInfo({
					...apptInfo,
					...newApptInfo,
				});
				setErrors({
					...errors,
					appointment: 'There was an error loading the calendar, please try again later.',
				});
			}
			initDataLayer('getBranchID', { branchID: newApiData.branchID });
			if (!apiData.appointmentStatus) {
				isFormComplete(
					{
						...formData,
						...apptInfo,
						...apiData,
						...address,
						...showroomInfo,
						...newApiData,
						...newApptInfo,
					},
					{ loading: false }
				); // current state followed by any changes
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.TerritoryZone, JSON.stringify(rtaData.TimezoneInfo), rtaData.BranchID, JSON.stringify(rtaData.Timeslots), rtaData.AppointmentType, rtaData.status]);

	useEffect(() => {
		// Checking appointment status
		if (rtaData.AppointmentStatus?.status) {
			if (rtaData.AppointmentStatus.status.toLowerCase() !== 'success') {
				dispatch(storeAppointment(formStatus.tempObject));
				setApiData({
					...apiData,
					appointmentStatus: rtaData.AppointmentStatus.status,
				});
				setFormStatus({ ...formStatus, tempStore: true });
				setContext('Error Data', {
					errorMessage: rtaData.AppointmentStatus?.errorMessage,
					httpStatusCode: rtaData.AppointmentStatus?.httpStatusCode,
				}),
					captureMessage('RTA Alerts - Appointment Error');
			} else {
				setApiData({
					...apiData,
					appointmentStatus: rtaData.AppointmentStatus.status,
				});
			}
		} else if (rtaData.submitError !== undefined && formStatus.tempStore !== rtaData.submitError) {
			setFormStatus({ ...formStatus, tempStore: true });
		} else if (rtaData.TempStatus !== undefined && apiData.appointmentStatus !== rtaData.TempStatus) {
			setApiData({
				...apiData,
				appointmentStatus: rtaData.TempStatus === true ? 'success' : 'failed',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [JSON.stringify(rtaData.AppointmentStatus), rtaData.submitError, rtaData.TempStatus]);
	// END: Might need to add checks inside a "mounted" if check

	useEffect(() => {
		if (apiData.appointmentStatus && apiData.appointmentStatus.toLowerCase() === 'success') {
			const state = {
				...formData,
				...address,
				...apiData,
				...apptInfo,
				...showroomInfo,
				// errors: errors,
				...formStatus,
				branchData: branchData,
			};
			initDataLayer('rta-form-submit');
			if (apptInfo?.startDate) {
				setCookie('mon_appt_info', apptInfo.startDate, new Date(apptInfo.startDate), true);
			}
			dispatch(resetRTACalls());
			navigate('/where-to-buy/schedule-consultation/confirmation/', {
				state: state,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [apiData.appointmentStatus]);

	useEffect(() => {
		// Watch for postal and postalCode mismatching
		if (showCalendar && !modalOpen && address.postalCode !== formData.postal && isValidPostal(address.postalCode)) {
			let open = false;
			if (getCountryFromPostal(formData.postal) === 'CAN') {
				if (formData.postal.replace(/\-| /gi, '') !== address.postalCode.replace(/\-| /gi, '')) {
					open = true;
				}
			} else if (formData.postal.substring(0, 5) !== address.postalCode.substring(0, 5)) {
				open = true;
			}
			if (open && !modalOpen) {
				setModalOpen(open);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [formData.postal, address.postalCode]);

	useEffect(() => {
		// watch when startDate has been set/changed
		if (apptInfo.startDate) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: apptInfo.firstCalendarLoad ? RTA_COMMON.BLOCK_TYPE.BLOCK : RTA_COMMON.BLOCK_TYPE.UNBLOCK_BLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			if (apptInfo.firstCalendarLoad) {
				setApptInfo({
					...apptInfo,
					firstCalendarLoad: false,
				});
			}
			dispatch(timeblock(customerData));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [apptInfo.startDate]);

	useEffect(() => {
		let interval;
		if (clock) {
			interval = setInterval(() => {
				let currCount = clockRef.current;
				if (currCount > 0) {
					setTimeLeft((currCount) => currCount - 1);
				} else {
					clearInterval(interval);
					refreshCalendar();
				}
			}, 1000);
		} else {
			clearInterval(interval);
		}

		return () => {
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [clock]);

	const clearTimer = (stopClock = true) => {
		if (typeof timeoutWarning.current === 'number') {
			clearTimeout(timeoutWarning.current);
			timeoutWarning.current = null;
		}
		if (stopClock) {
			setClock(false);
		}
	};

	const setTimer = () => {
		if (sessionTimerModal) {
			setSessionTimerModal(false);
		}
		clearTimer(false);
		timeoutWarning.current = setTimeout(
			() => {
				setSessionTimerModal(true);
				setClock(true);
				setTimeLeft(300);
			},
			1000 * 60 * 25
		);
	};

	const handleSessionModalClose = () => {
		setSessionTimerModal(false);
		setClock(false);
		setTimer();
		if (apptInfo.startDate) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: RTA_COMMON.BLOCK_TYPE.UNBLOCK_BLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			dispatch(timeblock(customerData));
		}
	};

	// PW-5453 - Added virtual
	const handleAppointmentChange = (e) => {
		dispatch(resetRTACalls());
		let path = '';

		if (isValidPostal(address.postalCode)) {
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME || apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
				if (apiData.hasShowroom) {
					path = 'js-ShowroomBanner';
				} else {
					path = 'js-Calendar';
				}
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
				path = 'js-NRT';
			}
			if (path !== '' && layout !== 'modal') {
				smoothScroll.scrollTo(path, -45);
			}

			if (apptInfo.startDate && !apiData.isInitial && !formStatus.loading) {
				const customerData = {
					oppNo: apiData.recID,
					start: apptInfo.startDate,
					end: apptInfo.endDate,
					type: RTA_COMMON.BLOCK_TYPE.UNBLOCK,
					zipcode: formData.postal,
					appType: apptInfo.appointmentType,
				};
				dispatch(timeblock(customerData));
			}
			const newApptInfo = {
				startDate: '',
				endDate: '',
				selectedDay: 0,
				selectedTimeslot: -1,
				arrayStartPosition: null,
				timeslotLength: null,
				firstCalendarLoad: true,
				appointmentType: e.target.value,
				aptSelected: e.target.value,
			};
			const newApiData = {
				isInitial: true,
				timeslots: [],
				territoryGroup: '',
				timezoneCode: '',
				branchID: '',
			};
			setApptInfo({
				...apptInfo,
				...newApptInfo,
			});
			setApiData({
				...apiData,
				...newApiData,
			});
			isFormComplete(
				{
					...formData,
					...apptInfo,
					...apiData,
					...address,
					...showroomInfo,
					...newApptInfo,
					...newApiData,
				},
				{ loading: true }
			); // current state followed by any changes
			dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, e.target.value));
		} else {
			setApptInfo({
				...apiData,
				appointmentType: e.target.value,
				aptSelected: e.target.value,
			});
		}
	};

	const handleChange = (e) => {
		const field = e.target.name;
		const value = e.target.value;
		setFormData({ ...formData, [field]: value });
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
		}); // current state followed by any changes
	};

	const handleBlur = (e) => {
		if (errors[e.target.name] && e.target.value) {
			setErrors({ ...errors, [e.target.name]: '' });
		}
	};

	const handlePreferenceChange = (e) => {
		const field = e.target.name;
		const value = e.target.value;
		const newApptInfo = {
			[field]: value,
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		}); // current state followed by any changes
	};

	const handleNumberChange = (name, value) => {
		let newFormStatus = {};
		let newAddress = {};
		let newApiData = {};
		let newApptInfo = {};

		const newFormData = {
			[name]: value,
		};
		setFormData({ ...formData, ...newFormData });
		isFormComplete(
			{
				...formData,
				...apptInfo,
				...apiData,
				...address,
				...showroomInfo,
				...newFormData,
				...newAddress,
				...newApiData,
				...newApptInfo,
			},
			newFormStatus
		); // current state followed by any changes
	};

	const handleCheck = (e) => {
		const field = e.target.id;
		const value = e.target.checked;
		const newFormData = {
			[field]: value,
		};
		if (errors[field] && value) {
			setErrors({ ...errors, [field]: null });
		}
		setFormData({ ...formData, ...newFormData });
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newFormData,
		}); // current state followed by any changes
	};

	const handleDaySelect = (start, length, id) => {
		const newApptInfo = {
			arrayStartPosition: start,
			timeslotLength: length,
			selectedDay: id,
			selectedTimeslot: -1,
			startDate: '',
			endDate: '',
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		}); // current state followed by any changes
	};

	const handleTimeslotSelect = (timeslot, index) => {
		const newApptInfo = {
			startDate: timeslot.START_DATE,
			endDate: timeslot.END_DATE,
			selectedTimeslot: index,
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		}); // current state followed by any changes
	};

	const validateEmail = (value) => {
		if (value.match(emailRegex) === null) {
			return false;
		}
		return true;
	};

	const validatePhone = (value) => {
		if (value.replace(phoneReplace, '').length !== 10) {
			return false;
		}
		return true;
	};

	const validateNumberField = (name, value) => {
		if (!value || value == '0') {
			return '';
		}
		if (isNaN(value)) {
			return `${name.charAt(0).toUpperCase() + name.substring(1)} must be a number`;
		}
		return '';
	};

	const loadCalendar = (e) => {
		setSpeedcurveCalendarTimingStart(performance.now());
		//search for zip
		e.preventDefault();
		dispatch(resetRTACalls());
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, address.postalCode, apptInfo.appointmentType));

		const newApiData = {
			waitingOnShowrooms: true,
			timeslots: [],
			territoryGroup: '',
			timezoneCode: '',
			branchID: '',
			isInitial: true,
		};
		setApiData({ ...apiData, ...newApiData });
		setFormStatus({
			...formStatus,
			loading: true,
			loadDisabled: true,
			submitDisabled: true,
		});
		setComplete(false);

		setTimeout(() => {
			let path = '';
			// PW-5453 - Added virtual
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME || apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
				if (apiData.hasShowroom) {
					path = 'js-ShowroomBanner';
				} else {
					path = 'js-Calendar';
				}
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
				path = 'js-NRT';
			}
			if (path !== '' && layout !== 'modal') {
				smoothScroll.scrollTo(path, -45);
			}
		}, 500);

		//Data Layer
		initDataLayer('load-calendar', {
			calendarZip: formData.postal,
			calendarAppType: apptInfo.appointmentType,
		});

		// Monetate - set page type to RTA_Calendar
		if (layout === 'default') {
			monetateDataLayer('setPageType', 'RTA_Calendar');
		}
	};

	const refreshCalendar = () => {
		// dispatch(resetRTACalls());
		let path = '';
		// PW-5453 - Added virtual
		if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME || apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
			if (apiData.hasShowroom) {
				path = 'js-ShowroomBanner';
			} else {
				path = 'js-Calendar';
			}
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
			path = 'js-Preference';
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
			path = 'js-NRT';
		}
		if (path !== '' && layout !== 'modal') {
			smoothScroll.scrollTo(path, -45);
		}
		if (apptInfo.startDate && !apiData.isInitial && !formStatus.loading) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: RTA_COMMON.BLOCK_TYPE.UNBLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			dispatch(timeblock(customerData));
		}
		setApptInfo({
			...apptInfo,
			startDate: '',
			endDate: '',
			selectedDay: 0,
			selectedTimeslot: -1,
			arrayStartPosition: null,
			timeslotLength: null,
			firstCalendarLoad: true,
		});
		setFormStatus({ ...formStatus, loading: true, submitDisabled: true });
		setApiData({
			...apiData,
			isInitial: true,
			timeslots: [],
			territoryGroup: '',
			timezoneCode: '',
			branchID: '',
		});
		setComplete(false);
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, apptInfo.appointmentType));
	};

	const validateForm = () => {
		let updatedErrors = {};

		if (!formData.firstname) {
			updatedErrors.firstname = RTA_ERRORS.FIRSTNAME_ERROR_HANDLER;
		}
		if (!formData.lastname) {
			updatedErrors.lastname = RTA_ERRORS.LASTNAME_ERROR_HANDLER;
		}
		if (!formData.email || (formData.email && !validateEmail(formData.email))) {
			updatedErrors.email = RTA_ERRORS.EMAIL_ERROR_HANDLER;
		}
		if (!formData.phone || (formData.phone && !validatePhone(formData.phone))) {
			updatedErrors.phone = RTA_ERRORS.PHONE_ERROR_HANDLER;
		}
		if (!formData.phonetype) {
			updatedErrors.phonetype = RTA_ERRORS.PHONE_TYPE_ERROR_HANDLER;
		}
		if (!apptInfo.appointmentType) {
			updatedErrors.appointmentType = RTA_ERRORS.APPOINTMENT_TYPE_ERROR_HANDLER;
		}
		if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.SHOWROOM, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType) && !apptInfo.startDate) {
			updatedErrors.appointment = RTA_ERRORS.DATE_TIME_ERROR_HANDLER;
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
			if (!apptInfo.preferenceDay) {
				updatedErrors.preferenceDay = RTA_ERRORS.PREFERRED_DAY_ERROR_HANDLER;
			}
			if (!apptInfo.preferenceTime) {
				updatedErrors.preferenceTime = RTA_ERRORS.PREFERRED_TIME_ERROR_HANDLER;
			}
		}
		if (!formData.privacyCheck) {
			updatedErrors.privacyCheck = RTA_ERRORS.PRIVACY_ERROR_HANDLER;
		}

		// if (!updatedErrors.windows) { updatedErrors.windows = validateNumberField('windows', formData.windows); }
		// if (!updatedErrors.doors) { updatedErrors.windows = validateNumberField('doors', formData.doors); }

		let formValid = true;
		if (Object.keys(updatedErrors).length > 0) {
			formValid = false;
			setErrors(updatedErrors);
		}
		return formValid;
	};

	const validateAddress = () => {
		let errors = {};
		if (!address.address1 || address.address1 === '') {
			errors.address1 = RTA_ERRORS.ADDRESS_LINE_1_ERROR_HANDLER;
		}
		if (!address.city || address.city === '') {
			errors.city = RTA_ERRORS.CITY_ERROR_HANDLER;
		}
		if (!address.state || address.state === '') {
			errors.state = RTA_ERRORS.STATE_ERROR_HANDLER;
		}
		if (!isValidPostal(address.postalCode)) {
			errors.postalCode = RTA_ERRORS.POSTAL_ERROR_HANDLER;
		}

		setAddressErrors(errors);

		if (Object.keys(errors).length > 0) {
			if (layout !== 'modal') window.scroll({ top: 0, behavior: 'smooth' });
			setForceAddressValidation(true);
			return false;
		} else {
			return true;
		}
	};

	const addressCheck = (object) => {
		if (!object.address1 || !object.city || !object.state || !isValidPostal(object.postalCode) || !object.country) {
			return true;
		}
		return false;
	};

	const isFormComplete = (object, statusChange = {}) => {
		let isComplete = true;
		if (!isValidPostal(object.postal) || !object.firstname || !object.lastname || !object.email || !object.phone || !object.phonetype) {
			isComplete = false;
		} else if (validateEmail(object.email) || validatePhone(object.phone)) {
			isComplete = false;
		} else if (validateNumberField(object.windows) || validateNumberField(object.doors)) {
			isComplete = false;
		} else if (!object.userEntered && (!object.addressValidated || addressCheck(object))) {
			isComplete = false;
		} else if (object.userEntered && (!object.userValidated || addressCheck(object))) {
			isComplete = false;
		} else if (!object.privacyCheck) {
			isComplete = false;
		} else if (!object.branchID) {
			isComplete = false;
		} else {
			if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(object.appointmentType)) {
				if (!object.startDate || !object.endDate) {
					isComplete = false;
				}
			} else if (object.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				if (!object.preferenceDay || !object.preferenceTime) {
					isComplete = false;
				}
			} else if (object.appointmentType !== RTA_COMMON.TYPE.NRT) {
				// appointmentType, not set
				isComplete = false;
			}
		}
		if (isComplete !== complete) {
			if (isComplete) {
				setErrors({});
			}
		}
		setComplete(isComplete);
		setFormStatus({
			...formStatus,
			...statusChange,
			submitDisabled: !isComplete,
		});
	};

	const autoCapitalize = (string) => string?.[0]?.toUpperCase() + string?.slice(1);

	const handleSubmit = (e) => {
		e.preventDefault();

		if (getCookie('CustomerType') === 'Professional' || (getCookie('CustomerType') == null && signInState?.customerType === 'professional')) {
			setLoading({
				active: true,
			});
		} else {
			setLoading({
				active: true,
			});
		}

		if (!validateForm()) {
			if (layout !== 'modal') window.scroll({ top: 0, behavior: 'smooth' });
			setLoading({ active: false, msg: null });
			return;
		}

		if (!validateAddress()) {
			if (layout !== 'modal') window.scroll({ top: 0, behavior: 'smooth' });
			setLoading({ active: false, msg: null });
			return;
		}

		let comments = formData.comments;
		if (isDesignworksLead) {
			comments += ' - design works lead';
		}

		// Submit form
		let urlstring = getSessionStorage('urlstring');

		if (!urlstring) {
			urlstring = 'parentSite:www.pella.com';
		}
		if (urlstring.search('RTA_Source') === -1 && window !== undefined && window.innerWidth < 790) {
			urlstring += '^^RTA_Source:Mobile';
		}
		if (apptInfo.appointmentType === 'showroom') {
			urlstring += '^^reqType:SHOWROOM';
		} else {
			urlstring += '^^reqType:web';
		}
		if (urlstring.search('host') === -1 && typeof window !== 'undefined') {
			urlstring += `^^host:${window.location.host}`;
		}

		let appointmentData = {
			oppNo: apiData.recID || randomId(),
			firstname: autoCapitalize(formData.firstname),
			lastname: autoCapitalize(formData.lastname),
			address: address.address1,
			address2: address.address2,
			country: address.country,
			city: address.city,
			state: address.state,
			zip: address.postalCode,
			dayphone: formData.phone,
			phonetype: formData.phonetype,
			// email not required for store or call center flows
			email: formData.email,
			urlstring: urlstring || null,
			apptsource: RTA_COMMON.INTERFACE.WEB,
			comments: comments || null,
			branchID: apiData.branchID,
			noOfWindows: formData.windows || null,
			noOfDoors: formData.doors || null,
			rtapage: referrer || formData.referrer || null,
			cartadd: getCookie('cartadd') || null,
			requestId: getLocalStorage('cheqResponse'),
			householdPets: formData.householdPets || undefined,
		};
		if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
			appointmentData['appttype'] = 'NRT';
			appointmentData['consentcall'] = 'N';
			appointmentData['consentsms'] = 'N';
			appointmentData['notifyemail'] = 'N';
			appointmentData['consenttophone'] = 'N';
			appointmentData['consenttomail'] = 'N';
			dispatch(appointment(appointmentData));
		} else {
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				appointmentData['preferenceDay'] = apptInfo.preferenceDay;
				appointmentData['preferenceTime'] = apptInfo.preferenceTime;
				appointmentData['notifyemail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentcall'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentsms'] = formData.contactCheck === true && formData.phonetype === 'MOBILE' ? 'Y' : 'N';
				appointmentData['consenttophone'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttomail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['appttype'] = 'Preferred';
				dispatch(appointment(appointmentData));
			}
			// PW-5453 - Added virtual
			else if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL, RTA_COMMON.TYPE.SHOWROOM].includes(apptInfo.appointmentType)) {
				appointmentData['start'] = apptInfo.startDate;
				appointmentData['end'] = apptInfo.endDate;
				appointmentData['notifyemail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentcall'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentsms'] = formData.contactCheck === true && formData.phonetype === 'MOBILE' ? 'Y' : 'N';
				appointmentData['consenttophone'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttomail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['appttype'] = 'RT';
				appointmentData['storename'] = showroomInfo.showroomName || null;
				// PW-5453 - Added virtual
				appointmentData['virtualapptmt'] = apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL ? true : false;

				if (apptInfo.appointmentType === RTA_COMMON.TYPE.SHOWROOM) {
					appointmentData['notifyemail'] = 'Y';
					appointmentData['consenttomail'] = 'Y';
				}

				dispatch(appointment(appointmentData));
			}
		}
		setCookie('ReqConsultation', true);
		setFormStatus({
			...formStatus,
			tempObject: appointmentData,
			submitDisabled: true,
		});
	};

	const closeShowroomAlert = () => {
		setFormStatus({ ...formStatus, showroomAlert: false });
	};

	// Save for possible future showroom appointments
	//
	// const handleShowroomSelect = (showroom) => {
	// 	dispatch(resetRTACalls());
	// 	dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, showroom.postalCode, apptInfo.appointmentType));
	// 	setShowroomInfo({
	// 		showroomName: showroom.name,
	// 		showroomAddress: `${showroom.address1}${showroom.address2 !== 'null' ? ' ' + showroom.address2 : ''}`,
	// 		showroomCity: showroom.city,
	// 		showroomState: showroom.state,
	// 		showroomCountry: showroom.country,
	// 		showroomZip: showroom.postalCode,
	// 		showroomEmail: showroom.emailAddress,
	// 		showroomPhone: showroom.phone,
	// 		showroomWeekHours: showroom.hourMon,
	// 		showroomSatHours: showroom.hourSat,
	// 		showroomSunHours: showroom.hourSun,
	// 	});
	// 	setApiData({ ...apiData, timeslots: [], isInitial: true });
	// 	setFormStatus({ ...formStatus, loading: true, submitDisabled: true });
	// 	setApptInfo({ ...apptInfo, appointmentType: RTA_COMMON.TYPE.SHOWROOM });
	// 	setComplete(false);

	// 	setTimeout(() => {
	// 		let path = '';
	// 		if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
	// 			path = 'js-Preference';
	// 		}
	// 		else if (apptInfo.appointmentType === RTA_COMMON.TYPE.SHOWROOM) {
	// 			path = 'js-Calendar';
	// 		}
	// 		if (path !== '') {
	// 			smoothScroll.scrollTo(path, -45);
	// 		}
	// 	}, 500);
	// };

	const handleInHomeAppointmentClick = (e) => {
		e.preventDefault();
		dispatch(resetRTACalls());
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, apptInfo.appointmentType));
		setShowroomInfo({
			showroomName: '',
			showroomAddress: '',
			showroomCity: '',
			showroomState: '',
			showroomCountry: '',
			showroomZip: '',
			showroomEmail: '',
			showroomPhone: '',
			showroomWeekHours: '',
			showroomSatHours: '',
			showroomSunHours: '',
		});
		setApiData({
			...apiData,
			waitingOnShowrooms: true,
			isInitial: true,
			timeslots: [],
			territoryGroup: '',
			timezoneCode: '',
			branchID: '',
		});
		setApptInfo({
			...apptInfo,
			appointmentType: RTA_COMMON.TYPE.HOME,
			startDate: '',
			endDate: '',
		});
		setFormStatus({
			...formStatus,
			showroomAlert: false,
			hideShowrooms: true,
			loading: true,
			submitDisabled: true,
		});
		setComplete(false);

		setTimeout(() => {
			let path = '';
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME) {
				if (apiData.hasShowroom) {
					path = 'js-ShowroomBanner';
				} else {
					path = 'js-Calendar';
				}
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
				path = 'js-NRT';
			}
			if (path !== '' && layout !== 'modal') {
				smoothScroll.scrollTo(path, -45);
			}
		}, 500);
	};

	// Save for possible future showroom appointments
	//
	// const handlePreferenceClick = (e) => {
	// 	e.preventDefault();
	// 	const customerData = {
	// 		oppNo: apiData.recID,
	// 		start: formData.startDate,
	// 		end: formData.endDate,
	// 		type: RTA_COMMON.BLOCK_TYPE.UNBLOCK,
	// 		zipcode: formData.postal,
	// 		appType: apptInfo.appointmentType
	// 	};
	// 	dispatch(timeblock(customerData));
	// 	setApptInfo({
	// 		...apptInfo,
	// 		appointmentType: RTA_COMMON.TYPE.PREFERENCE,
	// 		prevAppointmentType: apptInfo.appointmentType, // PW-5453 - Added virtual
	// 		startDate: '',
	// 		endDate: '',
	// 		selectedDay: 0,
	// 		selectedTimeslot: -1,
	// 		timeslotLength: 0,
	// 		firstCalendarLoad: true
	// 	});
	// 	setComplete(false);
	// 	setFormStatus({ ...formStatus, submitDisabled: true });

	// 	setTimeout(() => {
	// 		smoothScroll.scrollTo('js-Preference', -45);
	// 	}, 500);
	// };

	const handlePreferenceBackClick = (e) => {
		e.preventDefault();
		dispatch(resetRTACalls());
		setApptInfo({
			...apptInfo,
			appointmentType: apptInfo.prevAppointmentType ? apptInfo.prevAppointmentType : RTA_COMMON.TYPE.HOME, // PW-5453 - Added virtual
			prevAppointmentType: '', // PW-5453 - Added virtual
			startDate: '',
			endDate: '',
		});
		setApiData({
			...apiData,
			isInitial: true,
			timeslots: [],
			territoryGroup: '',
			branchID: '',
		});
		setFormStatus({ ...formStatus, loading: true });
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, apptInfo.appointmentType));

		setTimeout(() => {
			let path = '';
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			}
			// PW-5453 - Added virtual
			else if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType)) {
				path = 'js-Calendar';
			}
			if (path !== '' && layout !== 'modal') {
				smoothScroll.scrollTo(path, -45);
			}
		}, 500);
	};

	const handleModalClose = () => {
		// On close, set to previous address
		setModalOpen(false);
		setUsePreviousAddress(true);
		setComplete(false);
		setFormStatus({ ...formStatus, submitDisabled: true });
	};

	const handleModalZipChange = () => {
		dispatch(resetRTACalls());
		setModalOpen(false);
		const previousAddress = {
			...address,
			line_1: address.address1,
			line_2: address.address2,
			postcode: address.postalCode,
		};
		const newAppointmentType = apptInfo.aptSelected === RTA_COMMON.TYPE.VIRTUAL ? RTA_COMMON.TYPE.VIRTUAL : RTA_COMMON.TYPE.HOME;
		setFormData({
			...formData,
			postal: address.postalCode,
			previousAddress,
		});
		setApptInfo({
			...apptInfo,
			appointmentType: newAppointmentType,
			aptSelected: newAppointmentType,
		}); // PW-5453 - Added virtual
		setApiData({
			...apiData,
			branchID: '',
			territoryGroup: '',
			timezoneCode: '',
			timeslots: [],
			isInitial: true,
			waitingOnShowrooms: true,
		});
		setFormStatus({
			...formStatus,
			loading: true,
			hideShowrooms: true,
			showroomAlert: false,
			submitDisabled: true,
		});
		setComplete(false);
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, address.postalCode, newAppointmentType));
	};

	const handleAddressSubmit = (e) => {
		const isValid = validateAddress();
		if (isValid) {
			const previousAddress = {
				...address,
				line_1: address.address1,
				line_2: address.address2,
				postcode: address.postalCode,
			};
			loadCalendar(e);
			setShowCalendar(true);
			setFormData({
				...formData,
				postal: address.postalCode,
				previousAddress,
			});
		}
	};

	return (
		<>
			{layout !== 'modal' && branchData?.local_nav?.[0] && (
				<LocalNavTop
					branchData={branchData}
					linksArray={branchData.local_nav}
					isAnchors={false}
					currentUrl='/where-to-buy/schedule-consultation/'
					className={getSessionStorage('sem_referral') ? '!top-0' : ''}
				/>
			)}

			{layout !== 'modal' && (
				<Breadcrumbs
					data={{ active_page: 'Schedule Consultation' }}
					currentUrl='/where-to-buy/schedule-consultation/'
				/>
			)}

			<Container noPadding={layout === 'modal'}>
				{loading?.active && <LoadingOverlay />}
				<Row largeGutters={true}>
					<Column
						xs={12}
						lg={layout !== 'modal' ? 8 : null}
					>
						{layout !== 'modal' && (
							<>
								<h1 className={styles.rtaHeader_1}>{formStatus.hideShowrooms ? data.contentstackRtaInHomeAppointment.top_content[0].heading : data.contentstackRtaInHomeAppointment.top_content[3].heading}</h1>
								{formStatus.hideShowrooms ? (
									<div
										className={styles.rtaSubheading}
										dangerouslySetInnerHTML={{
											__html: data.contentstackRtaInHomeAppointment.top_content[0].subheading_1,
										}}
									/>
								) : (
									<div className={styles.rtaSubheading}>
										<p>
											{data.contentstackRtaInHomeAppointment.top_content[3].subheading}
											{/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- fix this */}
											<a
												href='#'
												gtm-link-text={data.contentstackRtaInHomeAppointment.top_content[3].link_text}
												gtm-link-type='Link'
												onClick={handleInHomeAppointmentClick}
											>
												{data.contentstackRtaInHomeAppointment.top_content[3].link_text}
											</a>
										</p>
									</div>
								)}
							</>
						)}

						{Object.keys(addressErrors).length > 0 && (
							<Alert
								type='error'
								aria-live='assertive'
								className='mb-4'
								hideClose
							>
								<p>Please correct the following errors:</p>
								<List className={styles.rtaAddressErrors}>
									{Object.values(addressErrors).map((err, index) => (
										<li
											key={index}
											dangerouslySetInnerHTML={{
												__html: err,
											}}
										/>
									))}
								</List>
							</Alert>
						)}

						{showCalendar && Object.keys(errors).length > 0 && (
							<Alert
								type='error'
								aria-live='assertive'
								className='mb-4'
								hideClose
							>
								<p>Please correct the following errors:</p>
								<List className={styles.rtaAddressErrors}>
									{Object.values(errors).map((err, index) => {
										if (!err) return null;
										return (
											<li
												key={index}
												dangerouslySetInnerHTML={{
													__html: err,
												}}
											/>
										);
									})}
								</List>
							</Alert>
						)}

						<form
							noValidate
							action=''
							method='POST'
						>
							<p className={styles.rtaRequiredText}>
								<span className={styles.rtaRequired}>*</span>
								Required Fields
							</p>

							{/* <h4 className={styles.rtaSubheading2}>
								<span className={styles.rtaRequired}>*</span>
								{data.contentstackRtaInHomeAppointment.top_content[2].subheading}
							</h4>

							<div className={styles.rtaCheckBox}>
								<div className={styles.rtaCheckboxCheck}>
									<Checkbox
										type='checkbox'
										group='apptOptions'
										id='appointmentType'
										className={styles.rtaCheckboxCheck}
										error={errors.appointmentType}
										errorText={RTA_ERRORS.APPOINTMENT_TYPE_REQUIRED}
										onChange={handleAppointmentChange}
										labelText={
											<div>
												<h3>
													{data.contentstackRtaInHomeAppointment.consultation_types[0].heading}
													<button onClick={e => e.preventDefault()}>
														<Icon name='info' className={styles.rtaCheckboxCheck_Icon} />
														<div>{data.contentstackRtaInHomeAppointment.consultation_types[0].description}</div>
													</button>
												</h3>
												<p>{data.contentstackRtaInHomeAppointment.consultation_types[0].subheading}</p>
											</div>
										}
										value='home'
										checked={apptInfo.aptSelected === RTA_COMMON.TYPE.HOME ? true : false}
										// defaultChecked={true}
									/>
								</div>
								<div className={styles.rtaCheckboxCheck}>
									<Checkbox
										type='checkbox'
										group='apptOptions'
										id='appointmentType1'
										className={styles.rtaCheckboxCheck}
										error={errors.appointmentType}
										errorText={RTA_ERRORS.APPOINTMENT_TYPE_REQUIRED}
										onChange={handleAppointmentChange}

										labelText={
											<div>
												<h3>
													{data.contentstackRtaInHomeAppointment.consultation_types[1].heading}
													<button onClick={e => e.preventDefault()}>
														<Icon name='info' className={styles.rtaCheckboxCheck_Icon} />
														<div>{data.contentstackRtaInHomeAppointment.consultation_types[1].description}</div>
													</button>
												</h3>
												<p>{data.contentstackRtaInHomeAppointment.consultation_types[1].subheading}</p>
											</div>
										}
										value='virtual'
										checked={apptInfo.aptSelected === RTA_COMMON.TYPE.VIRTUAL ? true : false}
									/>
								</div>
							</div> */}

							<p className={styles.rtaSubheading2}>Site Address Information</p>
							<AddressValidation
								allowInternational={true}
								defaultedPostalCode={formData.postal}
								addressComplete={[addressComplete, setAddressComplete]}
								addressInputs={{
									line1: {
										label: 'Street Address',
										xs: 12,
										sm: 6,
									},
									line2: { visible: true, xs: 12, sm: 6 },
									city: { xs: 12, sm: 6, lg: 4 },
									state: { xs: 12, sm: 6, lg: 4 },
									postalCode: { xs: 12, sm: 6, lg: 4 },
								}}
								addressInput={formData.previousAddress}
								addressOutput={[address, setAddress]}
								addressModified={[addressModified, setAddressModified]}
								addressValidate={[forceAddressValidation, setForceAddressValidation]}
								usePreviousAddress={[usePreviousAddress, setUsePreviousAddress]}
								className={styles.rtaAddressValidation}
							/>

							{!showCalendar && (
								<Button
									color={layout === 'modal' ? 'primary-alt' : 'primary'}
									onClick={handleAddressSubmit}
									id='js-addressSubmit'
								>
									{ctaText}
								</Button>
							)}

							{mounted && formStatus.showroomAlert && (
								<Row
									id='js-ShowroomBanner'
									className={styles.rtaShowroom_Alert}
								>
									<Column xs={12}>
										<Alert
											type='info'
											onClose={closeShowroomAlert}
											className='mb-4'
										>
											<MediaQuery minDeviceWidth={1030}>
												<div>
													{`${data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[0]} `}
													{/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- fix this */}
													<a
														href='#'
														gtm-link-text={data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[1]}
														gtm-link-type='link'
													>
														{data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[1]}
													</a>
													{` ${data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[2]}`}
												</div>
											</MediaQuery>
											<MediaQuery maxDeviceWidth={1029}>
												<div>
													{/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- fix this */}
													<a
														href='#'
														gtm-link-text={data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[1]}
														gtm-link-type='link'
													>
														{data.contentstackRtaInHomeAppointment.showroom_banner.hands_on[1]}
													</a>
												</div>
											</MediaQuery>
										</Alert>
									</Column>
								</Row>
							)}

							{showCalendar && (
								<Row className={styles.rtaCalendarHeader}>
									<Column xs={12}>
										{apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE ? (
											<div className={styles.rtaSection_Header}>
												<h2 className={styles.rtaSubheading2}>Select Preferred Day and Time</h2>
											</div>
										) : apptInfo.appointmentType !== RTA_COMMON.TYPE.NRT ? (
											<div
												className={styles.rtaSection_Header}
												dangerouslySetInnerHTML={{
													__html: data.contentstackRtaInHomeAppointment.section_headings[1],
												}}
											/>
										) : (
											''
										)}
									</Column>
								</Row>
							)}
							{/* Add AND check for home/showroom appointment */}
							{/* PW-5453 Added virtual */}
							{mounted && (apiData.isInitial || [RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.SHOWROOM, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType)) && showCalendar && (
								<React.Fragment>
									<Row className={styles.rtaHome_mb_15}>
										<Column xs='6' />
										<Column
											xs='6'
											className={styles.rtaCalendar_Clock_Wrapper}
										>
											<div>
												<Icon
													name='schedule'
													className={styles.rtaCalendar_Clock}
												/>
												All times in {apiData.timezoneCode}
											</div>
										</Column>
									</Row>

									<RTACalendarTimeWrapper
										id='js-Calendar'
										error={errors.appointment ? true : false}
									>
										{(apiData.isInitial || formStatus.loading) && <LoadingOverlay fixed={false} />}
										<Column
											xs='12'
											xl='6'
										>
											{(apiData.isInitial || apiData.timeslots.length > 0) && (
												<RTACalendar
													timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
													handleSelect={handleDaySelect}
													selectedDay={apiData.isInitial ? new Date().getDate() : apptInfo.selectedDay}
													disabled={formStatus.loading || apiData.isInitial}
												/>
											)}
										</Column>
										<Column
											xs='12'
											xl='6'
											className={styles.rtaCalendar_Column}
										>
											{apiData.isInitial || (apiData.timeslots.length > 0 && apptInfo.arrayStartPosition != null && apptInfo.timeslotLength != null) ? (
												<RTATimeslotTable
													timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
													arrayStartPosition={apiData.isInitial ? 0 : apptInfo.arrayStartPosition}
													selectedIndex={apiData.isInitial ? 0 : apptInfo.selectedTimeslot}
													timeslotLength={apiData.isInitial ? 3 : apptInfo.timeslotLength}
													handleSelect={handleTimeslotSelect}
													disabled={formStatus.loading || apiData.isInitial}
													initial={apiData.isInitial}
												/>
											) : (
												<RTATimeslotTable
													timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
													arrayStartPosition={0}
													selectedIndex={-1}
													timeslotLength={0}
													handleSelect={handleTimeslotSelect}
													disabled={formStatus.loading || apiData.isInitial}
													initial={apiData.isInitial}
												/>
											)}
										</Column>
									</RTACalendarTimeWrapper>
									{errors.appointment && <p className={styles.rtaDateError}>{RTA_ERRORS.DATE_TIME_REQUIRED}</p>}
								</React.Fragment>
							)}

							{mounted && apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE && (
								<Row
									verticalGutters={true}
									id='js-Preference'
								>
									<Column
										xs='12'
										md='6'
									>
										<Select
											error={errors.preferenceDay ? true : false}
											errorText={RTA_ERRORS.PREFERRED_DAY_REQUIRED}
											id='preferenceDay'
											onChange={handlePreferenceChange}
											onBlur={handleBlur}
											required
											labelText='Preferred Day'
											value={apptInfo.preferenceDay}
										>
											<option value=''>Select One...</option>
											<option value='Sunday'>Sunday</option>
											<option value='Monday'>Monday</option>
											<option value='Tuesday'>Tuesday</option>
											<option value='Wednesday'>Wednesday</option>
											<option value='Thursday'>Thursday</option>
											<option value='Friday'>Friday</option>
											<option value='Saturday'>Saturday</option>
											<option value='No Preference'>No Preference</option>
										</Select>
									</Column>
									<Column
										xs='12'
										md='6'
									>
										<Select
											error={errors.preferenceTime ? true : false}
											errorText={RTA_ERRORS.PREFERRED_TIME_REQUIRED}
											id='preferenceTime'
											onChange={handlePreferenceChange}
											onBlur={handleBlur}
											required
											labelText='Preferred Time'
											value={apptInfo.preferenceTime}
										>
											<option value=''>Select One...</option>
											<option value='Morning (8AM - 12PM)'>Morning (8AM - 12PM)</option>
											<option value='Afternoon (12PM-4PM)'>Afternoon (12PM-4PM)</option>
											<option value='Evening (4PM - 8PM)'>Evening (4PM - 8PM)</option>
											<option value='No Preference'>No Preference</option>
										</Select>
									</Column>

									{apiData.timeslots.length > 0 && (
										<Column xs='12'>
											<p>
												Got your schedule all figured out?
												{/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- fix this */}
												<a
													href='#'
													onClick={handlePreferenceBackClick}
												>
													Go back to calendar view
												</a>
											</p>
										</Column>
									)}
								</Row>
							)}

							{/* NRT Message */}
							{mounted && apptInfo.appointmentType === RTA_COMMON.TYPE.NRT && (
								<div
									className={styles.rtaNrtText}
									dangerouslySetInnerHTML={{
										__html: data.contentstackRtaInHomeAppointment.nrt_content,
									}}
								/>
							)}

							{/* Form Fields */}
							{showCalendar && (
								<Row className={styles.rtaContactInfo}>
									<Column xs='12'>
										<div
											className={styles.rtaSection_Header}
											dangerouslySetInnerHTML={{
												__html: data.contentstackRtaInHomeAppointment.section_headings[2],
											}}
										/>
									</Column>

									<Column
										xs='12'
										sm='6'
										className={styles.rtaInput}
									>
										<TextInput
											error={errors.firstname ? true : false}
											errorText={RTA_ERRORS.FIRSTNAME_REQUIRED}
											id='firstname'
											type='text'
											required
											aria-required='true'
											labelText='First Name'
											title='First Name'
											value={formData.firstname}
											onChange={handleChange}
											onBlur={handleBlur}
											autoComplete='given-name'
										/>
									</Column>

									<Column
										xs='12'
										sm='6'
										className={styles.rtaInput}
									>
										<TextInput
											error={errors.lastname ? true : false}
											errorText={RTA_ERRORS.LASTNAME_REQUIRED}
											id='lastname'
											type='text'
											required
											aria-required='true'
											labelText='Last Name'
											value={formData.lastname}
											onChange={handleChange}
											onBlur={handleBlur}
											autoComplete='family-name'
										/>
									</Column>

									<Column
										xs='12'
										md='6'
										className={styles.rtaInput}
									>
										<TextInput
											error={errors.email ? true : false}
											errorText={RTA_ERRORS.EMAIL_REQUIRED}
											id='email'
											type='text'
											required
											aria-required='true'
											onChange={handleChange}
											onBlur={handleBlur}
											labelText='Email Address'
											value={formData.email}
											autoComplete='email'
										/>
									</Column>

									<Column
										xs={12}
										sm={6}
										md={3}
										className={styles.rtaInput}
									>
										<TextInput
											error={errors.phone ? true : false}
											errorText={RTA_ERRORS.PHONE_REQUIRED}
											id='phone'
											type='tel'
											format='(###) ###-####'
											allowEmptyFormatting
											onValueChange={(values) => handleNumberChange('phone', values.formattedValue)}
											autoComplete='on'
											required
											aria-required='true'
											labelText='Phone'
											value={formData.phone}
											onBlur={handleBlur}
											counter={`${removePhoneNumberFormat(formData.phone).length}/10`}
										/>
									</Column>

									<Column
										xs={12}
										sm={6}
										md={3}
										className={styles.rtaInput}
									>
										<Select
											error={errors.phonetype ? true : false}
											errorText={RTA_ERRORS.PHONE_TYPE_REQUIRED}
											id='phonetype'
											value={formData.phonetype}
											onChange={handleChange}
											onBlur={handleBlur}
											required
											aria-required='true'
											labelText='Phone Type'
											autoComplete='off'
										>
											<option value=''>Please Select...</option>
											<option
												id='home'
												value='HOME'
											>
												Home
											</option>
											<option
												id='mobile'
												value='MOBILE'
											>
												Mobile
											</option>
											<option
												id='work'
												value='WORK'
											>
												Work
											</option>
										</Select>
									</Column>

									<Column
										xs='12'
										className='hidden'
									>
										<Select
											id='householdPets'
											value={formData.householdPets}
											onChange={handleChange}
											labelText='Do you have pets?'
											autoComplete='off'
										>
											<option value=''>Please Select...</option>
											<option
												id='householdPetsNo'
												value='No'
											>
												No
											</option>
											<option
												id='householdPetsYes'
												value='Yes'
											>
												Yes
											</option>
										</Select>
									</Column>

									<Column xs='12'>
										<TextArea
											id='comments'
											type='text'
											assistiveText='Anything else you  would like us to know about your project? (Example: How many windows and/or doors are you planning to replace?'
											onChange={handleChange}
											labelText='Project Details'
											value={formData.comments}
											groupClass={styles.rtaFormComments}
											autoComplete='off'
										/>
									</Column>

									<Column
										xs='12'
										className={styles.rtaTermsCheckboxCol}
									>
										<Checkbox
											type='checkbox'
											aria-required='true'
											id='privacyCheck'
											checked={formData.privacyCheck}
											onChange={handleCheck}
											error={errors.privacyCheck ? true : false}
											errorText={RTA_ERRORS.PRIVACY_REQUIRED}
											labelText={
												<div
													dangerouslySetInnerHTML={{
														__html: data.contentstackRtaInHomeAppointment.privacy,
													}}
												/>
											}
										/>
									</Column>

									<Column
										xs='12'
										className={styles.rtaTermsCheckboxCol}
									>
										<Checkbox
											type='checkbox'
											id='contactCheck'
											checked={formData.contactCheck}
											onChange={handleCheck}
											labelText={data.contentstackRtaInHomeAppointment?.contact_me}
										/>
										{data.contentstackRtaInHomeAppointment?.opt_in && (
											<div
												className='mb-4 text-sm 2xl:mb-0'
												dangerouslySetInnerHTML={{ __html: data.contentstackRtaInHomeAppointment.opt_in }}
											/>
										)}
									</Column>

									<Column
										xs='12'
										className={`${styles.rtaSubmit_Button} mt-4`}
									>
										<Button
											id='scheduleAppointment'
											type='button'
											color='primary'
											size='md'
											data-gtm-linktype='button'
											data-gtm-linktext='Schedule Appointment'
											onClick={handleSubmit}
										>
											Schedule Appointment
										</Button>
									</Column>
								</Row>
							)}
						</form>
					</Column>

					{layout !== 'modal' && (
						<Column
							className={styles.rtaCovidBanner}
							xs={12}
							lg={4}
						>
							<BranchOffer />
						</Column>
					)}
				</Row>
			</Container>

			{/* Postal Code Change Modal */}
			<Modal
				width={500}
				isOpen={modalOpen}
				onClose={handleModalClose}
				effect='slideup'
			>
				<Modal.Content>
					<Row
						justify='center'
						className={styles.rtaModal_Zip_Wrapper}
					>
						<Column
							xs='12'
							className={styles.rtaModal_margin}
						>
							<p className={styles.rtaModal_Zip_Text}>Changing your postal code may change appointment availability and require you to choose a new appointment date and time.</p>
						</Column>
						<Column
							xs='12'
							className={styles.rtaModal_margin}
						>
							<p className={styles.rtaModal_Zip_Question}>Are you sure you want to change your postal code?</p>
						</Column>
						<Column
							xs='12'
							lg='6'
							className={styles.rtaModal_margin}
						>
							<Button
								color='primary'
								onClick={handleModalZipChange}
								data-gtm-linktype='button'
								data-gtm-linktext='Yes'
								style={{ width: 175 }}
							>
								Yes
							</Button>
						</Column>
						<Column
							xs='12'
							lg='6'
							className={styles.rtaModal_margin}
						>
							<Button
								color='primary'
								type='button'
								onClick={handleModalClose}
								data-gtm-linktype='button'
								data-gtm-linktext='No'
								style={{ width: 175 }}
							>
								No
							</Button>
						</Column>
					</Row>
				</Modal.Content>
			</Modal>

			{/* Session Timeout Modal */}
			<Modal
				width={500}
				isOpen={sessionTimerModal}
				onClose={handleSessionModalClose}
				effect='slideup'
			>
				<Modal.Content>
					<Row
						justify='center'
						className={styles.rtaModal_Expire_Wrapper}
					>
						<Column
							xs='12'
							className={styles.rtaModal_margin}
						>
							<p className={styles.rtaModal_Expire_Question}>The calendar is filling up.</p>
						</Column>
						<Column
							xs='12'
							className={styles.rtaModal_margin}
						>
							<p className={styles.rtaModal_Expire_Text}>We don&apos;t want you to miss out. Confirm your date and time to secure your appointment.</p>
						</Column>
						<Column
							xs='12'
							lg='6'
							className={styles.rtaModal_margin}
						>
							<Button
								color='primary'
								type='button'
								onClick={handleSessionModalClose}
								data-gtm-linktype='button'
								data-gtm-linktext='Ok'
								style={{ width: 100 }}
							>
								Ok
							</Button>
						</Column>
					</Row>
				</Modal.Content>
			</Modal>
		</>
	);
};

ScheduleConsultation.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	layout: PropTypes.string,
	ctaText: PropTypes.string,
	referrer: PropTypes.string,
};

export default ScheduleConsultation;
